import React, { useState, useEffect } from "react";
import Image from "next/image";
import moment from "moment";
import LikeButton from "./LikeButton";
import CommentSection from "./CommentSection";
import Avatar from "../common/Avatar";
import { supabase } from "../../lib/supabase";

const PostCard = ({ post, currentUser, onPostUpdate }) => {
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [showImageModal, setShowImageModal] = useState(false);
  const [showComments, setShowComments] = useState(false);
  const [commentCount, setCommentCount] = useState(0);
  const [likeCount, setLikeCount] = useState(0);
  const [userHasLiked, setUserHasLiked] = useState(false);
  const [hasSummary, setHasSummary] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editText, setEditText] = useState(post.discription || "");
  const [isSavingEdit, setIsSavingEdit] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDeleted, setIsDeleted] = useState(false);
  const [localDescription, setLocalDescription] = useState(
    post.discription || ""
  );

  const formatCount = (n) =>
    new Intl.NumberFormat(undefined, {
      notation: "compact",
      maximumFractionDigits: 1,
    }).format(n || 0);

  const baseUrl = process.env.NEXT_PUBLIC_BASE_URL || "http://localhost:5000";

  useEffect(() => {
    // Use pre-fetched engagement data if available, otherwise fetch
    if (post.engagement) {
      setCommentCount(post.engagement.commentsCount || 0);
      setLikeCount(post.engagement.likeCount || 0);
      setUserHasLiked(Boolean(post.engagement.userHasLiked));
      setHasSummary(true);
      return;
    }

    // Fallback: fetch individually (for posts without pre-fetched data)
    let ignore = false;
    const loadCount = async () => {
      try {
        let headers = {};
        if (currentUser) {
          const {
            data: { session },
          } = await supabase.auth.getSession();
          const token =
            session?.access_token || localStorage.getItem("access_token");
          if (token) headers.Authorization = `Bearer ${token}`;
        }
        // Try summary endpoint first
        const res = await fetch(
          `${baseUrl}/api/gallery/posts/summary/${post._id}`,
          { headers }
        );
        const data = await res.json();
        if (ignore) return;
        if (data?.success && data?.data) {
          setCommentCount(data.data.commentsCount || 0);
          setLikeCount(data.data.likeCount || 0);
          setUserHasLiked(Boolean(data.data.userHasLiked));
          setHasSummary(true);
        } else {
          setHasSummary(false);
          // Fallback to comments endpoint
          const res2 = await fetch(
            `${baseUrl}/api/gallery/posts/comment/${post._id}`
          );
          const data2 = await res2.json();
          const count =
            data2?.data?.commentsCount ??
            data2?.commentsCount ??
            (Array.isArray(data2?.data?.comments)
              ? data2.data.comments.length
              : 0);
          setCommentCount(count || 0);
        }
      } catch (_) {
        // non-fatal; leave as 0
        setHasSummary(false);
      }
    };
    loadCount();
    return () => {
      ignore = true;
    };
  }, [post._id, post.engagement, baseUrl, currentUser?.id]);

  // Supabase metadata for current user (for self-display)
  const supa = currentUser?.user_metadata || {};
  const supaFirst = supa.first_name || supa.given_name || "";
  const supaLast = supa.last_name || supa.family_name || "";
  const supaComposed = [supaFirst, supaLast].filter(Boolean).join(" ");
  const supaName =
    supaComposed || supa.name || supa.full_name || currentUser?.email || "User";
  const supaAvatar = supa.avatar_url || supa.picture || "";

  // Creator display name
  const getCreatorDisplayName = (creator) => {
    if (!creator) return "Anonymous User";
    const local = creator.local || {};
    const google = creator.google || {};
    const facebook = creator.facebook || {};
    const fullName = [local.firstName, local.lastName]
      .filter(Boolean)
      .join(" ");
    return (
      fullName ||
      local.displayName ||
      google.displayName ||
      facebook.displayName ||
      local.email ||
      google.email ||
      facebook.email ||
      "Anonymous User"
    );
  };

  const formatDate = (dateString) => {
    try {
      return moment(dateString).fromNow();
    } catch (error) {
      return "some time ago";
    }
  };

  const getImageUrl = (imagePath) => {
    if (!imagePath) return "/images/placeholder-image.jpg";

    // Absolute URL
    if (imagePath.startsWith("http://") || imagePath.startsWith("https://")) {
      return imagePath;
    }

    // Relative path without leading slash
    if (!imagePath.startsWith("/")) {
      return `${process.env.NEXT_PUBLIC_BASE_URL || "http://localhost:5000"}/${imagePath}`;
    }

    // Leading slash path
    return `${process.env.NEXT_PUBLIC_BASE_URL || "http://localhost:5000"}${imagePath}`;
  };

  const handleImageError = (e) => {
    e.currentTarget.src = "/images/placeholder-image.jpg";
  };

  const openImageModal = (index) => {
    setSelectedImageIndex(index);
    setShowImageModal(true);
  };

  const navigateImage = (direction) => {
    if (direction === "next") {
      setSelectedImageIndex((prev) => (prev + 1) % post.assets.length);
    } else {
      setSelectedImageIndex(
        (prev) => (prev - 1 + post.assets.length) % post.assets.length
      );
    }
  };

  const handleInteraction = () => {
    if (onPostUpdate) {
      onPostUpdate();
    }
  };

  const isOwner = Boolean(
    post?.creator?.userId &&
      currentUser?.id &&
      post.creator.userId === currentUser.id
  );

  const toggleMenu = () => setMenuOpen((v) => !v);
  const closeMenu = () => setMenuOpen(false);

  const startEdit = () => {
    setEditText(localDescription || "");
    setIsEditing(true);
    closeMenu();
  };

  const cancelEdit = () => {
    setIsEditing(false);
  };

  const saveEdit = async () => {
    if (isSavingEdit) return;
    setIsSavingEdit(true);
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const token =
        session?.access_token || localStorage.getItem("access_token");
      const fd = new FormData();
      fd.append("discription", editText || "");
      const res = await fetch(`${baseUrl}/api/gallery/post/${post._id}`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      const result = await res.json();
      if (!res.ok || !result?.success) {
        throw new Error(result?.message || "Failed to update post");
      }
      setLocalDescription(editText || "");
      setIsEditing(false);
    } catch (e) {
      console.error("Edit post failed", e);
      // no toast lib imported here; minimal handling
      alert(e.message || "Failed to update post");
    } finally {
      setIsSavingEdit(false);
    }
  };

  const confirmAndDelete = async () => {
    if (!window.confirm("Delete this post? This cannot be undone.")) return;
    if (isDeleting) return;
    setIsDeleting(true);
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const token =
        session?.access_token || localStorage.getItem("access_token");
      const res = await fetch(`${baseUrl}/api/gallery/post/${post._id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      const result = await res.json();
      if (!res.ok || !result?.success) {
        throw new Error(result?.message || "Failed to delete post");
      }
      setIsDeleted(true);
      closeMenu();
    } catch (e) {
      console.error("Delete post failed", e);
      alert(e.message || "Failed to delete post");
    } finally {
      setIsDeleting(false);
    }
  };

  if (isDeleted) return null;

  return (
    <div className="bg-white dark:bg-jacarta-800 rounded-xl border border-jacarta-100 dark:border-jacarta-600 shadow-sm">
      {/* Header */}
      <div className="p-4 pb-0">
        <div className="flex items-center space-x-3 mb-3">
          <Avatar
            creator={post.creator}
            size="lg"
            nameFallback={
              post?.creator?.userId &&
              currentUser?.id &&
              post.creator.userId === currentUser.id
                ? supaName
                : ""
            }
          />
          <div className="flex-1">
            <div className="flex items-center space-x-2">
              <span className="font-display font-semibold text-jacarta-700 dark:text-white">
                {post?.creator?.userId &&
                currentUser?.id &&
                post.creator.userId === currentUser.id
                  ? supaName
                  : getCreatorDisplayName(post.creator)}
              </span>
            </div>
            <p className="text-sm text-jacarta-500 dark:text-jacarta-400">
              {formatDate(post.createdAt)}
            </p>
          </div>
          {isOwner && (
            <div className="relative ml-auto">
              <button
                onClick={toggleMenu}
                className="p-2 rounded-full hover:bg-jacarta-50 dark:hover:bg-jacarta-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/50"
                aria-haspopup="menu"
                aria-expanded={menuOpen}
                aria-label="Post actions"
              >
                <svg
                  className="w-5 h-5"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M12 13.5a1.5 1.5 0 100-3 1.5 1.5 0 000 3zM19.5 13.5a1.5 1.5 0 100-3 1.5 1.5 0 000 3zM4.5 13.5a1.5 1.5 0 100-3 1.5 1.5 0 000 3z"
                    fill="currentColor"
                  />
                </svg>
              </button>
              {menuOpen && (
                <div className="absolute right-0 mt-2 w-40 bg-white dark:bg-jacarta-800 border border-jacarta-100 dark:border-jacarta-600 rounded-md shadow-lg z-10">
                  <button
                    onClick={startEdit}
                    className="w-full text-left px-3 py-2 hover:bg-jacarta-50 dark:hover:bg-jacarta-700"
                  >
                    Edit Post
                  </button>
                  <button
                    onClick={confirmAndDelete}
                    className="w-full text-left px-3 py-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                    disabled={isDeleting}
                  >
                    {isDeleting ? "Deleting..." : "Delete Post"}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Content */}
        {!isEditing && localDescription && (
          <div className="mb-3">
            <p className="text-jacarta-700 dark:text-white whitespace-pre-wrap">
              {localDescription}
            </p>
          </div>
        )}
        {isEditing && (
          <div className="mb-3">
            <textarea
              value={editText}
              onChange={(e) => setEditText(e.target.value)}
              rows={4}
              maxLength={2000}
              className="w-full p-3 border border-jacarta-200 dark:border-jacarta-600 rounded-lg bg-transparent text-jacarta-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-accent/40"
              placeholder="Update your post text..."
            />
            <div className="mt-3 flex items-center gap-3">
              <button
                onClick={saveEdit}
                disabled={isSavingEdit}
                className="px-4 py-2 bg-accent text-white rounded-lg disabled:opacity-50"
              >
                {isSavingEdit ? "Saving..." : "Save"}
              </button>
              <button
                onClick={cancelEdit}
                className="px-4 py-2 text-jacarta-600 dark:text-jacarta-300"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Images */}
      {post.assets && post.assets.length > 0 && (
        <div className="relative">
          {post.assets.length === 1 ? (
            // Single
            <div className="cursor-pointer" onClick={() => openImageModal(0)}>
              <Image
                src={getImageUrl(post.assets[0])}
                alt="Post image"
                width={600}
                height={400}
                className="w-full h-64 sm:h-80 object-cover"
                onError={handleImageError}
              />
            </div>
          ) : post.assets.length === 2 ? (
            // Two side-by-side
            <div className="grid grid-cols-2 gap-1">
              {post.assets.map((asset, index) => (
                <div
                  key={index}
                  className="cursor-pointer"
                  onClick={() => openImageModal(index)}
                >
                  <Image
                    src={getImageUrl(asset)}
                    alt={`Post image ${index + 1}`}
                    width={300}
                    height={320}
                    className="w-full h-40 sm:h-48 object-cover"
                    onError={handleImageError}
                  />
                </div>
              ))}
            </div>
          ) : (
            // Grid
            <div className="grid grid-cols-2 gap-1">
              {post.assets.slice(0, 4).map((asset, index) => (
                <div
                  key={index}
                  className="relative cursor-pointer"
                  onClick={() => openImageModal(index)}
                >
                  <Image
                    src={getImageUrl(asset)}
                    alt={`Post image ${index + 1}`}
                    width={300}
                    height={240}
                    className={`w-full object-cover ${
                      index === 0 && post.assets.length > 2
                        ? "h-40 sm:h-48"
                        : index === 1 && post.assets.length > 2
                          ? "h-40 sm:h-48"
                          : "h-20 sm:h-24"
                    }`}
                    onError={handleImageError}
                  />
                  {index === 3 && post.assets.length > 4 && (
                    <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
                      <span className="text-white text-lg font-display font-bold">
                        +{post.assets.length - 4}
                      </span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Actions */}
      <div className="px-4">
        <div className="flex items-center justify-between py-2 border-b border-jacarta-100 dark:border-jacarta-600">
          <div className="flex items-center space-x-1">
            <LikeButton
              postId={post._id}
              currentUser={currentUser}
              onLikeChange={handleInteraction}
              onCountChange={setLikeCount}
              initialCount={likeCount}
              initialLiked={userHasLiked}
              prefetched={hasSummary || !!post.engagement}
            />
            {/* Comment toggle */}
            <button
              onClick={() => setShowComments((prev) => !prev)}
              aria-pressed={showComments}
              aria-label={showComments ? "Hide comments" : "Show comments"}
              className="inline-flex items-center gap-2 px-3 py-2 text-jacarta-500 hover:text-jacarta-700 hover:bg-jacarta-50 dark:hover:bg-jacarta-700 rounded-lg transition-colors"
            >
              <svg
                className="w-5 h-5 block"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M7 9H17M7 13H17M21 20L17.6757 18.3378C17.4237 18.2118 17.2977 18.1488 17.1656 18.1044C17.0484 18.065 16.9277 18.0365 16.8052 18.0193C16.6672 18 16.5263 18 16.2446 18H6.2C5.07989 18 4.51984 18 4.09202 17.782C3.71569 17.5903 3.40973 17.2843 3.21799 16.908C3 16.4802 3 15.9201 3 14.8V7.2C3 6.07989 3 5.51984 3.21799 5.09202C3.40973 4.71569 3.71569 4.40973 4.09202 4.21799C4.51984 4 5.0799 4 6.2 4H17.8C18.9201 4 19.4802 4 19.908 4.21799C20.2843 4.40973 20.5903 4.71569 20.782 5.09202C21 5.51984 21 6.0799 21 7.2V20Z"
                  stroke="currentColor"
                  strokeWidth={2}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <span className="font-display font-medium leading-none tabular-nums text-[12px] sm:text-sm">
                {formatCount(commentCount)}
              </span>
            </button>
          </div>
        </div>
        {/* Comments */}
        <div className="pb-2">
          <CommentSection
            postId={post._id}
            currentUser={currentUser}
            onCommentChange={handleInteraction}
            controlled
            isOpen={showComments}
            showToggleButton={false}
            onCountChange={setCommentCount}
          />
        </div>
      </div>

      {/* Image modal */}
      {showImageModal && post.assets && post.assets.length > 0 && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-90">
          <div className="relative max-w-4xl max-h-full p-4">
            {/* Close */}
            <button
              onClick={() => setShowImageModal(false)}
              className="absolute top-4 right-4 z-10 w-10 h-10 bg-black bg-opacity-50 text-white rounded-full flex items-center justify-center hover:bg-opacity-70 transition-colors"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>

            {/* Navigation */}
            {post.assets.length > 1 && (
              <>
                <button
                  onClick={() => navigateImage("prev")}
                  className="absolute left-4 top-1/2 transform -translate-y-1/2 z-10 w-10 h-10 bg-black bg-opacity-50 text-white rounded-full flex items-center justify-center hover:bg-opacity-70 transition-colors"
                >
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 19l-7-7 7-7"
                    />
                  </svg>
                </button>
                <button
                  onClick={() => navigateImage("next")}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 z-10 w-10 h-10 bg-black bg-opacity-50 text-white rounded-full flex items-center justify-center hover:bg-opacity-70 transition-colors"
                >
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </button>
              </>
            )}

            {/* Image */}
            <div className="flex items-center justify-center">
              <Image
                src={getImageUrl(post.assets[selectedImageIndex])}
                alt={`Post image ${selectedImageIndex + 1}`}
                width={800}
                height={600}
                className="max-w-full max-h-[80vh] object-contain"
                onError={handleImageError}
              />
            </div>

            {/* Counter */}
            {post.assets.length > 1 && (
              <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-black bg-opacity-50 text-white px-3 py-1 rounded-full text-sm">
                {selectedImageIndex + 1} of {post.assets.length}
              </div>
            )}
          </div>

          {/* Click outside to close */}
          <div
            className="absolute inset-0 -z-10"
            onClick={() => setShowImageModal(false)}
          />
        </div>
      )}
    </div>
  );
};

export default PostCard;
